import type { MaybeRefOrGetter, Ref } from "vue";
import type { QueryClient } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import { computed, toValue, unref } from "vue";
import { getApi } from "./api";

export type LearnedRouteCircleEndpoint = {
  type: "circle";
  center: { lat: number; lng: number };
  radius_m: number;
};

export type LearnedRouteGeofenceEndpoint = {
  type: "geofence";
  geofence_id: number;
};

export type LearnedRouteEndpoint =
  | LearnedRouteCircleEndpoint
  | LearnedRouteGeofenceEndpoint;

export type LearnedRouteRequestBody = {
  origin: LearnedRouteEndpoint;
  intermediates?: LearnedRouteEndpoint[];
  destination: LearnedRouteEndpoint;
  travel_mode: "DRIVE";
  max_alternatives_per_segment?: number;
};

export type LearnedRouteLegMetaDto = {
  provider?: string;
  distance_m?: number;
  duration_s?: number;
  sample_count?: number;
};

export type LearnedRouteLegDto = {
  polyline?: { encodedPolyline?: string };
  meta?: LearnedRouteLegMetaDto;
};

export type LearnedRouteDto = {
  legs?: LearnedRouteLegDto[];
};

export type LearnedRoutePlanDto = {
  routes?: LearnedRouteDto[];
};

export function accountLearnedRoutesQueryKey(
  accountId: number,
  body: LearnedRouteRequestBody,
): readonly ["v2", "account-learned-routes", number, LearnedRouteRequestBody] {
  return ["v2", "account-learned-routes", accountId, body];
}

export async function loadAccountLearnedRoutesData(
  accountId: number,
  body: LearnedRouteRequestBody,
  signal?: AbortSignal,
): Promise<LearnedRoutePlanDto> {
  if (accountId == null || accountId === 0) {
    return { routes: [] };
  }
  const { api, baseURL } = getApi();
  const res = await api.post(
    `/api/v2/accounts/${accountId}/learned-routes`,
    body,
    { baseURL, signal },
  );
  return (res.data?.data ?? { routes: [] }) as LearnedRoutePlanDto;
}

function useGetAccountLearnedRoutes(
  accountId: Ref<number | null | undefined> | number,
  body: Ref<LearnedRouteRequestBody | null> | LearnedRouteRequestBody | null,
  options: { enabled: MaybeRefOrGetter<boolean> },
) {
  return useQuery({
    queryKey: [
      "v2",
      "account-learned-routes",
      () => unref(accountId),
      () => unref(body),
    ],
    queryFn: async ({ signal }): Promise<LearnedRoutePlanDto> => {
      const aid = unref(accountId);
      const request = unref(body);
      if (aid == null || aid === 0 || request == null) {
        return { routes: [] };
      }
      return loadAccountLearnedRoutesData(aid, request, signal);
    },
    enabled: computed(() => {
      const aid = unref(accountId);
      const request = unref(body);
      const paramsOk = aid != null && aid !== 0 && request != null;
      return paramsOk && toValue(options.enabled);
    }),
  });
}

async function fetchAccountLearnedRoutesQueryData(
  queryClient: QueryClient,
  accountId: number,
  body: LearnedRouteRequestBody,
  signal?: AbortSignal,
): Promise<LearnedRoutePlanDto> {
  return queryClient.fetchQuery({
    queryKey: accountLearnedRoutesQueryKey(accountId, body),
    queryFn: ({ signal: querySignal }) =>
      loadAccountLearnedRoutesData(accountId, body, signal ?? querySignal),
    staleTime: 0,
  });
}

const getAccountLearnedRoutes = Object.assign(useGetAccountLearnedRoutes, {
  queryKey: accountLearnedRoutesQueryKey,
  load: loadAccountLearnedRoutesData,
  fetchQueryData: fetchAccountLearnedRoutesQueryData,
});

export default getAccountLearnedRoutes;
